import { apiClient, API_CONFIG } from '../config/api'

// trajectoryService — Fleet Portal port of the legacy
// balise-management/frontend/src/services/trajectoryService.ts. Calls the
// Java BM backend's TrajectoryResource through the /api/bm proxy. The
// Java endpoint @Path is /trajectory (the Jersey servlet is mounted at
// /api/*, so the wire path is /api/trajectory/{lockCode}); going through
// fleet-api as /api/bm/trajectory/{lockCode} preserves the bearer auth
// rewrite that fleet-api injects for service-to-service calls.

const BM = API_CONFIG.BM_BASE_URL

export interface TrajectoryPoint {
  event_id: number
  timestamp: string
  gps_timestamp?: string
  latitude: number
  longitude: number
  altitude?: number
  speed?: number
  heading?: number
  gps_valid?: boolean
  satellite_count?: number
  gps_accuracy?: number
  battery_level?: number
  battery_voltage?: number
  signal_strength?: number
  network_operator?: string
  network_type?: string
  lock_status?: string
  device_temperature?: number
  event_type?: string
  event_subtype?: string
  event_severity?: string
  lock_code: string
  brand?: string
  device_name?: string
  universal_id?: string
}

export interface TrajectoryData {
  universal_id?: string
  trajectory: TrajectoryPoint[]
  total_points: number
  start_date?: string
  end_date?: string
  limit?: number
}

export interface TrajectoryStats {
  universal_id?: string
  lock_code?: string
  total_events: number
  first_event?: string
  last_event?: string
  start_date?: string
  end_date?: string
  avg_speed?: number
  max_speed?: number
  avg_battery_level?: number
  min_battery_level?: number
  avg_signal_strength?: number
  active_days?: number
  critical_events?: number
  error_events?: number
  data_source?: string
}

export interface TrajectoryFilters {
  startDate?: string
  endDate?: string
  limit?: number
}

// The Java TrajectoryDao expects "yyyy-MM-dd HH:mm:ss" or ISO strings.
// We accept both — caller-supplied ISO strings are passed through as-is.
function isValidDateFormat(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(s)
}

function formatDateForApi(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function buildParams(filters?: TrajectoryFilters): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  if (!filters) return out
  if (filters.startDate) {
    out.startDate = isValidDateFormat(filters.startDate)
      ? filters.startDate
      : formatDateForApi(new Date(filters.startDate))
  }
  if (filters.endDate) {
    out.endDate = isValidDateFormat(filters.endDate)
      ? filters.endDate
      : formatDateForApi(new Date(filters.endDate))
  }
  if (filters.limit) out.limit = filters.limit
  return out
}

export const trajectoryService = {
  async getBaliseTrajectory(lockCode: string, filters?: TrajectoryFilters): Promise<TrajectoryData> {
    if (!lockCode || !lockCode.trim()) throw new Error('Lock code is required')
    const { data } = await apiClient.get(
      `${BM}/trajectory/${encodeURIComponent(lockCode)}`,
      buildParams(filters),
    )
    return data as TrajectoryData
  },

  async getTrajectoryStats(lockCode: string, filters?: TrajectoryFilters): Promise<TrajectoryStats> {
    if (!lockCode || !lockCode.trim()) throw new Error('Lock code is required')
    const { data } = await apiClient.get(
      `${BM}/trajectory/${encodeURIComponent(lockCode)}/stats`,
      buildParams(filters),
    )
    return data as TrajectoryStats
  },

  // Best-effort discovery for the empty-results card. The Java debug
  // endpoint returns a free-form Map; we extract fields defensively
  // because the backend shape is not contractually stable.
  async discoverAvailableDataPeriods(lockCode: string): Promise<{
    hasData: boolean
    totalPoints?: number
    earliestDate?: string
    latestDate?: string
    suggestedPeriods?: string[]
  }> {
    if (!lockCode || !lockCode.trim()) return { hasData: false }
    try {
      const { data } = await apiClient.get(`${BM}/trajectory/${encodeURIComponent(lockCode)}/debug`)
      const total = Number(data?.total_points ?? data?.totalPoints ?? 0)
      if (!Number.isFinite(total) || total <= 0) return { hasData: false }
      return {
        hasData: true,
        totalPoints: total,
        earliestDate: data?.earliest_date ?? data?.earliestDate ?? data?.first_event,
        latestDate: data?.latest_date ?? data?.latestDate ?? data?.last_event,
        suggestedPeriods: Array.isArray(data?.suggested_periods)
          ? data.suggested_periods
          : Array.isArray(data?.suggestedPeriods)
            ? data.suggestedPeriods
            : ['7d', '30d'],
      }
    } catch {
      return { hasData: false }
    }
  },
}

export default trajectoryService
